<template>
    <v-app :dir="direction" :class="mainClasses">
        <v-content >
            <router-view v-if="isReady"
                         :class='{"dialog-open": isDialogOpen}'/>

            <IconsLoader :size="loaderSize" class="app-loader" v-if="showLoader"/>
        </v-content>
      <component is="style">{{brandingColors}}</component>
    </v-app>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import {postMessageService} from "@/modules/common/services";
import {IconsLoader} from "@/modules/common/components";
import {urlUtil, stringUtil} from '@/modules/common/utils';
import moment from "moment";
import colorUtil from '@vcita/design-system/utils/colorUtil.js';
import { datadogRum } from '@datadog/browser-rum';

export default {
  name: "App",
  components: {
    IconsLoader
  },
  data() {
    return {
      isReady: false,
      firstNotification: true,
      fullScreenOptions: {
        isDisplayingFullScreen: false,
        isHidingPageBar: false,
      },
    }
  },
  async created () {
    this.$frontageApi.listen('iframe-set-fullscreen', this.setFullScreen);
    // When vue app is ready and account service is ready (AngularJS) - listen to notification center initialize
    this.$frontageApi.listen('initNotificationCenter', this.initNotification);
    this.$frontageApi.listen('reload-account', this.reloadAccount);
    this.$frontageApi.listen('reload-staff', this.reloadStaffs);
    this.$frontageApi.listen('reload-account-start', this.setLoadingAccount);
    this.$frontageApi.listen('logout', this.handleLogout);
    this.$frontageApi.listen('update-store', this.updateStore);
    this.$setKeys('en', window.app_config.translations.en)
    const lang = urlUtil.getParameterByName("lang")
    if (lang) {
      await this.$loadLang(lang);
      this.$ds.setLocale(lang);
      this.$setLang(lang)
      moment.locale(lang)
      this.$vuetify.lang.current = lang
    }

    if(this.lang === 'he'){
      this.$vuetify.rtl = true
    }
    this.checkForcedFeatureFlag()
    this.$store.dispatch({type: "initWindowLocation"})
    postMessageService.post('app_loaded')
    this.isReady = true;

    // Set vuetify theme colors according to brand
    this.$vuetify.theme.themes.light.primary = colorUtil.rgbToHex(this.primaryColor);
    this.$vuetify.theme.themes.light.secondary = {
      base: colorUtil.rgbToHex(this.secondaryColor),
      lighten1: colorUtil.pSBC(0.6, colorUtil.rgbToHex(this.secondaryColor)),
      lighten2: colorUtil.pSBC(0.76, colorUtil.rgbToHex(this.secondaryColor)),
      lighten3: colorUtil.pSBC(0.93, colorUtil.rgbToHex(this.secondaryColor)),
    }

    // Init datadog
    if (this.$hasFeature('add_datadog')) {
        // Save datadogRum globally
        window.datadogRum = datadogRum;

        datadogRum.init({
          clientToken: 'pube9a8020d41d2a002725b152f2269943c',
          applicationId: '4ca47f08-1407-4a21-a404-48d9d2ad8e4d',
          site: 'datadoghq.com',
          service: 'frontage',
          env: `${window.vue_config.VUE_APP_ENV}`,
          sessionSampleRate: 90,
          sessionReplaySampleRate: 85,
          trackUserInteractions: true,
          usePartitionedCrossSiteSessionCookie: true,
          trackResources: true,
          trackLongTasks: true,
          trackSessionAcrossSubdomains: true,
          allowedTracingUrls: [ /\/api2/i ],
          defaultPrivacyLevel: 'mask-user-input',
          });
    }
  },
  beforeDestroy() {
    this.$frontageApi.remove('iframe-set-fullscreen', this.setFullScreen)
    this.$frontageApi.remove('logout', this.handleLogout)
  },
  destroyed() {
    this.$frontageApi.remove('initNotificationCenter', this.initNotification)
    this.$frontageApi.remove('update-current-notification-counter', this.emitCurrentCounter);
  },
  computed: {
    ...mapGetters(['lang', 'isMobile', 'isMobileApp', 'dialogStatus', 'showLoader', 'isChromeIos', 'isiPhone', 'isiPad']),
    ...mapGetters("accountModule", ["pendo","isStar","account", "staff", "firstDayOfWeek"]),
    ...mapGetters("brandModule", ["secondaryColor","primaryColor","brandColor","textColor"]),
    mainClasses() {
      return {
        rtl: this.lang === 'he',
        chromeIos: this.isChromeIos,
        iPhone: this.isiPhone,
        iPad: this.isiPad,
        mobile: this.isMobile,
        mobileApp: this.isMobileApp,
        'is-displaying-fullscreen': this.fullScreenOptions.isDisplayingFullScreen,
        'is-hiding-page-bar': this.fullScreenOptions.isHidingPageBar,
        'is-mobile-display': this.isMobile || this.isMobileApp,
      }
    },
    direction() {
      return this.lang === 'he' ? 'rtl' : 'ltr'
    },
    isDialogOpen(){
      return this.dialogStatus.isOpen
    },
    loaderSize() {
      return this.isMobile ? "sm" : "md"
    },
    brandingColors() {
      //Convert brand colors to hex
      let secondary = stringUtil.pSBC(0, this.secondaryColor, '#000000')
      let primary = stringUtil.pSBC(0, this.primaryColor, '#000000')
      let brandColor = stringUtil.pSBC(0, this.brandColor, '#000000')
      let textColor = stringUtil.pSBC(0, this.textColor, '#000000')
      return `
        body {
        --brand-color-secondary: ${secondary};
        --brand-color-primary: ${primary};
        --brand-color: ${brandColor};
        --brand-text-color: ${textColor};
        //convert to (red, green, blue) string that can be used in rgba();
        --brand-color-secondary-rgb: ${stringUtil.hexToRgb(secondary)};
        --brand-color-primary-rgb: ${stringUtil.hexToRgb(primary)};
        --brand-color-rgb: ${stringUtil.hexToRgb(brandColor)};
        --brand-text-color-rgb: ${stringUtil.hexToRgb(textColor)};
        }`
    },
    momentInitProp() {
      return [this.lang, this.firstDayOfWeek].join('')
    }
  },
  methods: {
    ...mapActions("accountModule", ["fetchAccount", "setLoading", "reloadStaff"]),
    ...mapActions("notificationCenterModule", ["emitCurrentCounter"]),
    handleLogout(skipCleanup) {
      if(skipCleanup) {
        return
      }
      localStorage.removeItem(`CACHED_MEETINGS`);
      localStorage.removeItem(`SLOTS_TO_MILLISECOND_MAP_VERSION`);
      localStorage.removeItem(`weeklyAvailabilityException`);
      localStorage.removeItem(`weeklyAvailability`);
    },
    setFullScreen(fullScreenOptions) {
      this.fullScreenOptions = fullScreenOptions
    },
    checkForcedFeatureFlag() {
      if (!this.isMobileApp &&  window.vue_config.VUE_APP_ENV !== "production") {
        let forceFeatureFlag = urlUtil.getParameterByName("forceFeatureFlagName",window.parent.location.href)
        if (forceFeatureFlag) {
          this.$store.commit("addForceFeature",forceFeatureFlag);
       }
      }
    },

    initNotification() {
      // firstNotification - for app_loaded
      // badge - for new vue instance created each badge click
      if (this.firstNotification && !urlUtil.getParameterByName("badge")) {
        this.$frontageApi.listen('update-current-notification-counter', this.emitCurrentCounter);
        this.fetchAccount()
        this.firstNotification = false
      }

    },
    setLoadingAccount(value = true) {
      this.setLoading(value)
    },
    reloadAccount() {
      this.fetchAccount()
    },
    reloadStaffs() {
      this.reloadStaff()
    },
    updateStore(data) {
      this.$store.dispatch(data.type, data.payload);
    },
  },
  watch: {
    momentInitProp: {
      handler(val) {
        moment.locale(this.lang, {
          week: {
            dow: this.firstDayOfWeek,
          }
        })
      },
      immediate: true
    },
    primaryColor: function(newVal, oldVal) {
      // Set vuetify theme colors according to brand
      this.$vuetify.theme.themes.light.primary = colorUtil.rgbToHex(newVal);
    },
    secondaryColor: function(newVal, oldVal) {
      // Set vuetify theme colors according to brand
      this.$vuetify.theme.themes.light.secondary = {
        base: colorUtil.rgbToHex(newVal),
        lighten1: colorUtil.pSBC(0.6, colorUtil.rgbToHex(newVal)),
        lighten2: colorUtil.pSBC(0.76,colorUtil.rgbToHex(newVal)),
        lighten3: colorUtil.pSBC(0.93, colorUtil.rgbToHex(newVal))
      }
   },
  }
}
</script>

<style>
@import './plugins/tinymce/skins/ui/oxide/content.min.css';
@import './plugins/tinymce/skins/ui/oxide/skin.min.css';
</style>
<style lang="scss" >
    #app{
        background: transparent;
        min-height: 100vh;
      --application-padding-top: 0px;
      --application-padding-inline-start: 0px;

      .v-application--wrap {
        padding-top: var(--application-padding-top);
        padding-inline-start: var(--application-padding-inline-start);
      }
    }

    .v-content{
      max-height: 100%;
    }

    .rtl {
        direction: rtl;
        text-align: right;
    }

    .app-loader {
      position: fixed;
      z-index: 1;
      top: 0;
    }
</style>
